import { VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { LeaveVoicemailNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { ExtensionSelector } from '../core/ExtensionSelector'
import { VoicemailMessageSelector } from '../core/VoicemailMessageSelector'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useLeaveVoicemailNode } from './hooks/useLeaveVoicemailNode'

export interface LeaveVoicemailNodeProps extends NodeProps {
  extensionMap?: Map<number, ExtensionInstance>
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: extensionsList and soundClips are provided via Jotai atoms
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

const LeaveVoicemailNodeComponent: React.FC<LeaveVoicemailNodeProps> = ({
  id,
  data,
  extensionMap: _extensionMap,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as LeaveVoicemailNodeData & Record<string, unknown>
  const {
    currentExtensionId,
    currentSoundClipId,
    handleExtensionChange,
    handleSoundClipChange,
    hasData,
  } = useLeaveVoicemailNode(id, nodeData)
  const t = useSwitchboardT()

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.leave_voicemail',
        'LEAVE VOICEMAIL',
      )}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      sourceHandlePosition={null}
    >
      <VStack align="stretch" gap={2}>
        <ExtensionSelector
          label={text(t, 'switchboard.core.extensionLabel', 'Extension')}
          value={currentExtensionId}
          onChange={handleExtensionChange}
          placeholder={text(
            t,
            'switchboard.core.selectExtension',
            'Select extension...',
          )}
          filterMode="voicemail"
        />

        <VoicemailMessageSelector
          t={t}
          label={text(t, 'switchboard.nodes.leaveVoicemail.message', 'Message')}
          value={currentSoundClipId}
          onChange={handleSoundClipChange}
        />
      </VStack>
    </BaseNode>
  )
}

export const LeaveVoicemailNode = React.memo(
  LeaveVoicemailNodeComponent,
  nodePropsAreEqual,
)
